import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal, GoldLine } from "@/components/Reveal";
import { GUIDES, waLink, WA_MESSAGES } from "@/config/site";

const GuideArticle = () => {
    const { slug } = useParams();
    const guide = GUIDES.find((g) => g.slug === slug);
    const others = GUIDES.filter((g) => g.slug !== slug).slice(0, 3);

    if (!guide)
        return (
            <main className="pt-40 pb-32 text-center min-h-screen">
                <p className="font-serif italic text-3xl text-sand">This guide could not be found.</p>
                <Link to="/guide" className="inline-block mt-8 text-gold text-xs tracking-[0.25em] uppercase border-b border-gold/50 pb-1" data-testid="guide-back-link">All Guides →</Link>
            </main>
        );

    return (
        <main className="pt-28 sm:pt-36 pb-24 min-h-screen">
            <Seo
                title={`${guide.title} | The BSK Jewellery Guide`}
                siteName="BSK Jewellers"
                description={guide.excerpt}
                image={guide.image}
                type="article"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: guide.title,
                    description: guide.excerpt,
                    author: { "@type": "Organization", name: "BSK Jewellers" },
                }}
            />
            <article className="max-w-3xl mx-auto px-5 sm:px-8">
                <nav className="text-[10px] tracking-[0.25em] uppercase text-sand mb-10" data-testid="guide-breadcrumb">
                    <Link to="/guide" className="hover:text-gold transition-colors">Jewellery Guide</Link>
                    <span className="mx-2 text-gold/50">/</span>
                    <span className="text-gold">{guide.title}</span>
                </nav>
                <Reveal>
                    <p className="eyebrow">{guide.readTime} read</p>
                    <h1 className="font-serif text-4xl sm:text-5xl leading-[1.1] mt-4">{guide.title}</h1>
                    <GoldLine delay={0.15} className="w-20 my-7" />
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="aspect-[16/8] overflow-hidden border border-gold/15 mb-12">
                        <img src={guide.image} alt={guide.title} className="w-full h-full object-cover" />
                    </div>
                </Reveal>
                {guide.sections.map((s, i) => (
                    <Reveal key={i} delay={0.05}>
                        <section className="mb-10">
                            <h2 className="font-serif text-2xl sm:text-3xl text-gold-bright/90">{s.h}</h2>
                            <p className="text-sand text-sm sm:text-base leading-relaxed mt-4">{s.p}</p>
                        </section>
                    </Reveal>
                ))}
                <Reveal>
                    <div className="border border-gold/25 bg-charcoal/50 p-8 mt-14 text-center" data-testid="guide-cta">
                        <p className="font-serif text-2xl">Have a question about your jewellery?</p>
                        <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noreferrer" data-testid="guide-whatsapp-btn" className="inline-block mt-5 bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 hover:bg-gold-bright transition-colors">
                            Ask an Expert on WhatsApp
                        </a>
                    </div>
                </Reveal>
            </article>
            <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-24">
                <h2 className="font-serif text-3xl mb-8">Continue <span className="italic gold-text">Reading</span></h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {others.map((g) => (
                        <Link key={g.slug} to={`/guide/${g.slug}`} data-testid={`guide-related-${g.slug}`} className="group border border-gold/10 hover:border-gold/50 transition-colors p-6 bg-charcoal/40">
                            <p className="text-[10px] tracking-[0.25em] uppercase text-gold">{g.readTime} read</p>
                            <h3 className="font-serif text-xl text-ivory mt-2 group-hover:text-gold transition-colors">{g.title}</h3>
                        </Link>
                    ))}
                </div>
            </div>
        </main>
    );
};

export default GuideArticle;
